/**
 * @module NotificationItem
 * @package @geenius/notifications-react
 * @description Renders a single inbox row for the Tailwind React variant. The
 * component keeps the primary read action on a real button while exposing
 * secondary link and dismiss actions separately for accessibility.
 */

import { motion } from '@geenius/motion/react'
import type { Notification } from '@geenius/notifications/shared'
import {
  formatNotificationRelativeTime,
  getNotificationIcon,
} from '@geenius/notifications/shared'
import type { JSX } from 'react'
import { useNotificationCopy } from '../lib/notificationCopy'
import {
  getNotificationAccentStyle,
  notificationIconFrameVariants,
} from '../lib/notificationStyles'
import { cn } from '../lib/utils'

/**
 * Props for a single notification row.
 */
export interface NotificationItemProps {
  notification: Notification
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  className?: string
}

/**
 * Renders a single notification row with optional read and dismiss actions.
 *
 * @param props Notification row props.
 * @returns The rendered notification row.
 */
export function NotificationItem({
  notification,
  onRead,
  onDismiss,
  className,
}: NotificationItemProps): JSX.Element {
  const { t } = useNotificationCopy()
  const isUnread = notification.status === 'unread'

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -1 }}
      transition={{ duration: 0.2 }}
      className={cn(
        'flex gap-4 px-5 py-4 transition-colors',
        isUnread
          ? 'bg-[var(--gn-notifications-panel-surface)]'
          : 'bg-transparent hover:bg-[var(--gn-notifications-panel-surface-muted)]',
        className,
      )}
    >
      {notification.actorAvatar ? (
        <img
          src={notification.actorAvatar}
          alt=""
          className="h-9 w-9 flex-shrink-0 rounded-full border border-border object-cover"
        />
      ) : (
        <span
          className={notificationIconFrameVariants()}
          style={getNotificationAccentStyle(notification.type)}
        >
          {getNotificationIcon(notification.type)}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <button
          type="button"
          onClick={() => {
            if (isUnread) {
              onRead?.(notification.id)
            }
          }}
          disabled={!isUnread || !onRead}
          aria-label={
            isUnread
              ? t('notifications.item.markReadAria', { title: notification.title })
              : t('notifications.item.notificationAria', { title: notification.title })
          }
          className="w-full rounded-xl text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-default disabled:opacity-100"
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <div
                className={cn(
                  'text-[13px] leading-snug tracking-wide text-foreground',
                  isUnread ? 'font-bold' : 'font-medium',
                )}
              >
                {notification.title}
              </div>
              <div className="mt-1 text-[11px] font-medium leading-snug text-muted-foreground">
                {notification.body}
              </div>
            </div>
            {isUnread ? (
              <span
                aria-hidden="true"
                className="mt-1 h-2 w-2 flex-shrink-0 rounded-full bg-primary shadow-sm"
              />
            ) : null}
          </div>
        </button>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
            {formatNotificationRelativeTime(notification.createdAt, t)}
          </span>
          {notification.actionUrl ? (
            <>
              <span className="text-[10px] text-muted-foreground">•</span>
              <a
                href={notification.actionUrl}
                className="text-[10px] font-bold uppercase tracking-widest text-primary no-underline transition-colors hover:text-primary"
              >
                {notification.actionLabel ?? t('notifications.common.view')}
              </a>
            </>
          ) : null}
          {onDismiss ? (
            <button
              type="button"
              onClick={() => onDismiss(notification.id)}
              className="ml-auto rounded-md px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            >
              {t('notifications.common.dismiss')}
            </button>
          ) : null}
        </div>
      </div>
    </motion.article>
  )
}
